/* eslint-disable @typescript-eslint/no-misused-promises */
import { type FC, useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Stack,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { DialogBox } from 'components';
import Buttons from 'components/elements/Buttons';
import { strings } from 'i18n/Strings';
import { type SelectChangeEvent } from '@mui/material/Select/SelectInput';
import apiUtils from 'utils/ApiUtil';
import { colorTheme } from 'theme';

interface EditWatchListModelProps {
  open: boolean;
  setOpenWatchlist: (open: boolean) => void;
  watchlistSanctionsHit: boolean | null | undefined;
  watchlistPepHit: boolean | null | undefined;
  cutomerId: number;
  handleSuccess: () => void;
}

interface WatchlistStatus {
  sanctions: boolean | '';
  pep: boolean | '';
}

const watchlistFields: Array<{
  key: keyof WatchlistStatus;
  label: string;
}> = [
  {
    key: 'sanctions',
    label: strings.displayText.updateSanctionsWatchlistStatus,
  },
  {
    key: 'pep',
    label: strings.displayText.updatePEPWatchlistStatus,
  },
];

const EditWatchListModel: FC<EditWatchListModelProps> = ({
  open,
  setOpenWatchlist,
  watchlistSanctionsHit,
  watchlistPepHit,
  cutomerId,
  handleSuccess,
}) => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [watchlistStatus, setWatchlistStatus] = useState<WatchlistStatus>({
    sanctions: '',
    pep: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    if (open) {
      setWatchlistStatus({
        sanctions: watchlistSanctionsHit ?? '',
        pep: watchlistPepHit ?? '',
      });
    }
  }, [open, watchlistSanctionsHit, watchlistPepHit]);

  const handleChange =
    (field: keyof WatchlistStatus) =>
    (event: SelectChangeEvent<boolean | ''>) => {
      const value =
        event.target.value === ''
          ? ''
          : event.target.value === 'true' || event.target.value === true;

      setWatchlistStatus((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleClose = () => {
    setOpenWatchlist(false);
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleUpdate = async () => {
    const { pep, sanctions } = watchlistStatus;
    if (pep === true || sanctions === true) {
      setError('Please select a valid option.');
      return;
    }
    setError(null);
    setIsLoading(true);
    await new Promise((resolve, reject) => {
      apiUtils
        .putWithToken(`customer/${cutomerId}/watchlist`, {
          pep,
          sanctions,
        })
        .then((res) => {
          resolve(res);
        })
        .catch((error) => {
          reject(error);
        });
    });
    setIsLoading(false);
    handleSuccess();
  };

  return (
    <DialogBox
      open={open}
      screenWidth="sm"
      title={strings.displayText.updateWatchlistStatus}
      onClose={handleClose}
      className={''}
      fullWidth={false}
    >
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {watchlistFields.map(({ key, label }) => (
          <Grid item xs={12} key={key}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={7}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {label}
                </Typography>
              </Grid>

              <Grid item xs={5}>
                <FormControl fullWidth size="small">
                  <Select
                    value={watchlistStatus[key]}
                    onChange={handleChange(key)}
                    disabled={
                      key === 'sanctions'
                        ? !watchlistSanctionsHit
                        : key === 'pep'
                        ? !watchlistPepHit
                        : false
                    }
                  >
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Buttons
              variant="contained"
              onClick={handleClose}
              sx={{
                bgcolor: colors.gray[900],
              }}
            >
              Cancel
            </Buttons>
            <Buttons
              variant="contained"
              onClick={handleUpdate}
              disabled={isLoading}
              endIcon={isLoading ? <CircularProgress size={15} /> : null}
            >
              {isLoading ? 'Updating...' : 'Update'}
            </Buttons>
          </Stack>
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Grid>
      </Grid>
    </DialogBox>
  );
};

export default EditWatchListModel;
